import { useMemo } from 'react'
import { Grid, IconButton, Checkbox, Hidden, CircularProgress } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faEye, faWrench, faCommentDots, faCloud, faPaperclip } from '@fortawesome/free-solid-svg-icons'
import CircleCheckedFilled from '@mui/icons-material/CheckCircle'
import CircleUnchecked from '@mui/icons-material/RadioButtonUnchecked'
import { segundosHoras } from '../../utils/NumberUtils'
import { useExecutionReportContext } from '../../context'

/**
 * Renders a single row (item) in the Pauta (checklist) detail.
 *
 * @param {object} props - The component props.
 * @param {object} props.item - The checklist item data.
 * @param {number} props.index - The index of the item in the list.
 * @param {object} props.report - The full report object.
 * @param {boolean} props.isOperator - True if the current user is an operator.
 * @param {object} props.userData - The current user's data.
 * @param {boolean} props.puedeTerminarJornada - Indicates if the shift can be ended.
 * @param {string} props.indexGroup - The currently selected group index.
 * @param {function} props.openDialog - Handler to open the ReportDataDialog for this item.
 * @param {function} props.openBacklogMessage - Handler to open the BacklogMessageDialog for this item.
 * @param {function} props.changeState - Handler to change the checked state of the item.
 * @param {boolean} props.canEdit - Indicates if the user has general edit permissions.
 * @param {boolean} props.isOnline - Indicates if the application is online.
 */
const PautaItemRow = ({
    item,
    index,
    report,
    isOperator,
    userData,
    puedeTerminarJornada,
    indexGroup,
    openDialog,
    openBacklogMessage,
    changeState,
    canEdit,
    isOnline
}) => {
    const { syncQueue, executionReport } = useExecutionReportContext()

    const isItemLoading = useMemo(() => {
        if (item.loading) return true
        if (!syncQueue || syncQueue.length === 0 || !executionReport?._id) return false

        return syncQueue.some(q =>
            q.executionId === executionReport._id &&
            String(q.page) === String(indexGroup) &&
            q.index === index
        )
    }, [item.loading, syncQueue, executionReport?._id, indexGroup, index])

    // Ensure item.isChecked is initialized for rendering
    if (item.isChecked === undefined) {
        item.isChecked = false
    }

    // Pre-process obs01 for cleaner rendering
    const processedObs01 = (item.obs01 || '')
        .replace('____________________________________.', '________.')
        .replace('__________________________', '________')
        .replace('_________________', '________')

    return (
        <Grid key={index} container style={{ borderBottomStyle: 'solid', borderBottomWidth: 1, borderBottomColor: '#ccc' }}>
            <Grid item xs={1} md={'auto'}>
                <div style={{ width: 30 }}>
                    <p style={{ fontSize: 12, margin: 0, textAlign: 'center' }}> <strong>{index + 1}.-</strong> </p>
                </div>
            </Grid>
            <Grid item xs={11} md={1}>
                <div style={{ width: '100%' }}>
                    <p style={{ fontSize: 12, margin: 0, textAlign: 'center', wordBreak: 'break-word' }}> {item.workteamdesc} </p>
                </div>
            </Grid>
            <Grid item xs={12} md={'auto'}>
                <div style={{ width: 150 }}>
                    <p style={{ fontSize: 12, margin: 0, textAlign: 'left' }}> {item.taskdesc} </p>
                </div>
            </Grid>
            <Grid item xs={12} md={2}>
                <div style={{ width: '100%' }}>
                    <p style={{ fontSize: 12, margin: 0, textAlign: 'justify' }}>
                        {processedObs01}
                    </p>
                </div>
            </Grid>
            {/* N° Parte, Cantidad, Tipo Rpto, Parámetro a Medir/Obtenido - Maintenance specific */}
            <Hidden mdDown>
                {(report.reportType === 'Mantención') && (
                    <>
                        <Grid item xl={'auto'} md={'auto'}>
                            {(['%', '°C', 'CM', 'H', 'KM', 'KPA', 'M', 'MM', 'N', 'PSI', 'RPM', 'T', 'V'].includes(item.unidad))
                                ? (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 100 }}>N/A</p>
                                )
                                : (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 100 }}>{item.partnumberUtl || item.partnumber_utl}</p>
                                )}
                        </Grid>
                        <Grid item xl={'auto'} md={'auto'}>
                            {(['UN', 'C/U', 'BL', 'LT', 'KG', 'JG', 'GLN'].includes(item.unidad))
                                ? (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 100 }}>{item.cantidad} {item.unidad}</p>
                                )
                                : (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 100 }}>N/A</p>
                                )}
                        </Grid>
                        <Grid item xl={'auto'} md={'auto'}>
                            {(['UN', 'C/U', 'BL', 'LT', 'KG', 'JG', 'GLN'].includes(item.unidad))
                                ? (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 60 }}>{(item.unidadData !== null && item.unidadData !== undefined) ? item.unidadData : '___'} {item.unidad}</p>
                                )
                                : (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 60 }}>N/A</p>
                                )}
                        </Grid>
                        <Grid item xl={'auto'} md={'auto'}>
                            {(item.idtypeutlPartnumber === '*' || item.idtypeutl_partnumber === '*')
                                ? (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 60 }}>N/A</p>
                                )
                                : (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 60 }}>{item.idtypeutlPartnumber || item.idtypeutl_partnumber}</p>
                                )}
                        </Grid>
                        <Grid item xl={'auto'} md={'auto'}>
                            {(['%', '°C', 'CM', 'H', 'KM', 'KPA', 'M', 'MM', 'N', 'PSI', 'RPM', 'T', 'V'].includes(item.unidad))
                                ? (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 100 }}>{item.partnumberUtl || item.partnumber_utl}</p>
                                )
                                : (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 100 }}>N/A</p>
                                )}
                        </Grid>
                        <Grid item xl={'auto'} md={'auto'}>
                            {(['%', '°C', 'CM', 'H', 'KM', 'KPA', 'M', 'MM', 'N', 'PSI', 'RPM', 'T', 'V'].includes(item.unidad))
                                ? (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 60 }}>
                                        {(item.unidadData !== null && item.unidadData !== undefined) ? ((item.partnumberUtl === 'HOROMETRO EQUIPO' || item.partnumber_utl === 'HOROMETRO EQUIPO') ? segundosHoras(item.unidadData) : item.unidadData) : '___'} {item.unidad}
                                    </p>
                                )
                                : (
                                    <p style={{ fontSize: 12, padding: 5, margin: 0, textAlign: 'center', width: 60 }}>N/A</p>
                                )}
                        </Grid>
                    </>
                )}
            </Hidden>
            <Grid item xs={6} sm={6} md={1}>
                <Grid container >
                    <Grid item xl={4} style={{ textAlign: 'left', paddingLeft: 5 }}>
                        <IconButton onClick={() => { openDialog(item, index) }}>
                            <FontAwesomeIcon style={{ fontSize: 20 }} icon={(canEdit && puedeTerminarJornada) ? faPen : faEye} />
                        </IconButton>
                    </Grid>
                    <Grid item xl={4} style={{ textAlign: 'left', paddingLeft: 5 }}>
                        <IconButton style={{ backgroundColor: (item.backlogList && item.backlogList.length > 0) ? 'red' : 'transparent' }} onClick={() => { openBacklogMessage(item, index) }}>
                            <FontAwesomeIcon style={{ fontSize: 20 }} icon={faWrench} color={(item.backlogList && item.backlogList.length > 0) ? 'white' : 'rgba(0, 0, 0, 0.54)'} />
                        </IconButton>
                    </Grid>
                    <Grid item xl={4} style={{ textAlign: 'left' }}>
                        {/* Placeholder for other icons/actions if needed */}
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={6} sm={6} md={1}>
                {
                    !puedeTerminarJornada
                        ? (
                            <Checkbox
                                checked={!!(item.isChecked || item.isWarning)}
                                disabled
                                style={{ transform: 'scale(1.2)' }}
                                icon={<CircleUnchecked style={{ fontSize: 20 }} />}
                                checkedIcon={<CircleCheckedFilled style={{ fontSize: 20, color: item.isWarning ? '#EAD749' : '#27AE60' }} />}
                            />
                        )
                        : (
                            <Checkbox
                                value={!!item.isChecked}
                                checked={!!(item.isChecked || item.isWarning)}
                                disabled={!canEdit}
                                onClick={() => { changeState(item, index, indexGroup) }}
                                style={{ transform: 'scale(1.2)' }}
                                icon={<CircleUnchecked style={{ fontSize: 20 }} />}
                                checkedIcon={<CircleCheckedFilled style={{ fontSize: 20, color: item.isWarning ? '#EAD749' : '#27AE60' }} />}
                            />
                        )
                }
                {item.haveMessage && <IconButton style={{ padding: 1 }} disabled><FontAwesomeIcon style={{ fontSize: 20 }} icon={faCommentDots} /></IconButton>}
                {(isItemLoading) && <CircularProgress size={20} color="primary" style={{ padding: 1 }} />}
                {((item.isChecked || item.isWarning || item.haveClip || (item.backlogList && item.backlogList.length > 0)) && !isItemLoading && isOnline) && <IconButton style={{ padding: 1 }} disabled><FontAwesomeIcon style={{ fontSize: 20 }} icon={faCloud} /></IconButton>}
                {!item.messages && <IconButton style={{ padding: 1 }} disabled><FontAwesomeIcon style={{ fontSize: 20, color: 'transparent' }} icon={faCommentDots} /></IconButton>}
                {item.haveClip && <IconButton style={{ padding: 1 }} disabled><FontAwesomeIcon style={{ fontSize: 20 }} icon={faPaperclip} /></IconButton>}
                {!item.haveClip && <IconButton style={{ padding: 1 }} disabled><FontAwesomeIcon style={{ fontSize: 20, color: 'transparent' }} icon={faPaperclip} /></IconButton>}
            </Grid>
        </Grid>
    )
}

export default PautaItemRow
